import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  WorkflowConfigForm,
  defaultWorkflowConfig,
  eventSourceOptions,
} from "@/types/workflow-config";
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { useDeviceStore, Device, DeviceTelemetry } from '@/stores/devices/device-store';
import { workflowFetch } from "@/services/api/workflowApi";
import { buildWorkflowApiRoute } from "@/utils/workflow-utils";
import {
  Workflow,
  Zap,
  Activity,
  Info,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Database,
  Settings,
  ArrowLeft,
  Save,
  Plus,
  RefreshCw,
  Pencil,
  Cpu,
  Wifi,
  WifiOff,
  Trash2,
  MoreVertical,
  Terminal,
  RotateCcw,
  Power,
  Cloud,
  HardDrive,
  ArrowRightLeft,
} from "lucide-react";
import { DeviceExecutionLog } from '@/components/workflow/DeviceExecutionLog';
import { WorkflowTransferDialog } from '@/components/workflow/WorkflowTransferDialog';
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type WorkflowSummaryStatus = "draft" | "published" | "archived" | "deprecated";
type WorkflowSummaryType = "server" | "device" | "agent";
const WORKFLOW_API_PLACEHOLDER =
  import.meta.env.VITE_WORKFLOW_BACKEND_URL || "/workflow-api";

interface WorkflowSummary {
  id: string;
  name: string;
  description: string;
  status: WorkflowSummaryStatus | string;
  type: WorkflowSummaryType;
  owner: string;
  runCount: number;
  lastRun?: string;
  lastUpdated?: string;
}

interface WorkflowListApiResponse {
  success: boolean;
  data?: WorkflowApiItem[];  // Backend returns array directly
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasMore: boolean;
  };
  message?: string;
  error?: string;
}

interface WorkflowApiUser {
  _id?: string;
  name?: string;
  email?: string;
}

interface WorkflowApiItem {
  id?: string;
  _id?: string;
  name?: string;
  description?: string;
  status?: string;
  type?: string;
  executionCount?: number;
  lastExecutedAt?: string;
  updatedAt?: string;
  created_by?: WorkflowApiUser;
  metadata?: {
    category?: string;
  };
}

interface WorkflowMutationApiResponse {
  success?: boolean;
  message?: string;
  error?: string;
  data?: {
    id?: string;
    _id?: string;
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getOptionalString = (value: unknown, key: string): string | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const candidate = value[key];
  return typeof candidate === "string" ? candidate : undefined;
};

const getOptionalNumber = (value: unknown, key: string): number | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const candidate = value[key];
  return typeof candidate === "number" ? candidate : undefined;
};

const parseWorkflowApiUser = (value: unknown): WorkflowApiUser | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  return {
    _id: getOptionalString(value, "_id"),
    name: getOptionalString(value, "name"),
    email: getOptionalString(value, "email"),
  };
};

const parseWorkflowApiItem = (value: unknown): WorkflowApiItem => ({
  id: getOptionalString(value, "id"),
  _id: getOptionalString(value, "_id"),
  name: getOptionalString(value, "name"),
  description: getOptionalString(value, "description"),
  status: getOptionalString(value, "status"),
  type: getOptionalString(value, "type"),
  executionCount: getOptionalNumber(value, "executionCount"),
  lastExecutedAt: getOptionalString(value, "lastExecutedAt"),
  updatedAt: getOptionalString(value, "updatedAt"),
  created_by: parseWorkflowApiUser(isRecord(value) ? value.created_by : undefined),
  metadata: isRecord(value) && isRecord(value.metadata)
    ? {
        category: getOptionalString(value.metadata, "category"),
      }
    : undefined,
});

const parseWorkflowListApiResponse = (value: unknown): WorkflowListApiResponse => {
  const data = isRecord(value) ? value.data : undefined;
  const pagination = isRecord(value) ? value.pagination : undefined;

  return {
    success: isRecord(value) && value.success === true,
    data: Array.isArray(data) ? data.map(parseWorkflowApiItem) : undefined,
    pagination: isRecord(pagination)
      ? {
          page: getOptionalNumber(pagination, "page") ?? 0,
          limit: getOptionalNumber(pagination, "limit") ?? 0,
          total: getOptionalNumber(pagination, "total") ?? 0,
          pages: getOptionalNumber(pagination, "pages") ?? 0,
          hasMore: pagination.hasMore === true,
        }
      : undefined,
    message: getOptionalString(value, "message"),
    error: getOptionalString(value, "error"),
  };
};

const parseWorkflowMutationApiResponse = (value: unknown): WorkflowMutationApiResponse => ({
  success: isRecord(value) ? value.success === true : false,
  message: getOptionalString(value, "message"),
  error: getOptionalString(value, "error"),
  data: isRecord(value) && isRecord(value.data)
    ? {
        id: getOptionalString(value.data, "id"),
        _id: getOptionalString(value.data, "_id"),
      }
    : undefined,
});

const isWorkflowSummaryStatus = (value: string): value is WorkflowSummaryStatus =>
  value === "draft" ||
  value === "published" ||
  value === "archived" ||
  value === "deprecated";

const isWorkflowSummaryType = (value: string): value is WorkflowSummaryType =>
  value === "server" || value === "device" || value === "agent";

function getEventSourceConfigKey(
  sourceId: string,
): (
  | "enableDatabaseEvents"
  | "enableWebhookEvents"
  | "enableTimerEvents"
  | "enableManualEvents"
) | undefined {
  switch (sourceId) {
    case "database":
      return "enableDatabaseEvents";
    case "webhook":
      return "enableWebhookEvents";
    case "timer":
      return "enableTimerEvents";
    case "manual":
      return "enableManualEvents";
    default:
      return undefined;
  }
}

export const WorkflowConfigurationPage: React.FC = () => {
  const { projectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const { currentContext } = useOrganizationStore();

  const [config, setConfig] = useState<WorkflowConfigForm>(defaultWorkflowConfig);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [isLoadingWorkflows, setIsLoadingWorkflows] = useState(true);
  const [workflowsError, setWorkflowsError] = useState<string | null>(null);
  const [workflowsTotal, setWorkflowsTotal] = useState<number | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renamingWorkflow, setRenamingWorkflow] = useState<WorkflowSummary | null>(null);
  const [newWorkflowName, setNewWorkflowName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  // Delete workflow dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<WorkflowSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Transfer workflow dialog state
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [workflowToTransfer, setWorkflowToTransfer] = useState<WorkflowSummary | null>(null);

  // Register device dialog state
  const [registerDeviceDialogOpen, setRegisterDeviceDialogOpen] = useState(false);
  const [registerDeviceId, setRegisterDeviceId] = useState('');
  const [registerDeviceName, setRegisterDeviceName] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);

  // Device details dialog state
  const [deviceDetailsDialogOpen, setDeviceDetailsDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

  // Device store
  const {
    devices,
    counts: deviceCounts,
    isLoading: devicesLoading,
    fetchDevices: refreshDevices,
    deleteDevice,
    registerDevice,
    initialize: initializeDeviceStore,
    isInitialized: devicesInitialized,
    getTelemetry,
    telemetry: telemetryMap, // Subscribe to telemetry Map for re-renders
    setDeviceLogging,
    restartDevice,
  } = useDeviceStore();

  // Initialize device store on mount
  useEffect(() => {
    if (!devicesInitialized) {
      initializeDeviceStore();
    }
  }, [devicesInitialized, initializeDeviceStore]);

  // Helper functions for device status display
  const getDeviceStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'online': return 'default';      // green
      case 'offline': return 'secondary';   // gray
      case 'deploying': return 'outline';   // blue border
      case 'error': return 'destructive';   // red
      default: return 'outline';
    }
  };

  const getDeviceStatusIcon = (status: string) => {
    return status === 'online' ? (
      <Wifi className="w-4 h-4 text-green-500" />
    ) : (
      <WifiOff className="w-4 h-4 text-gray-400" />
    );
  };

  // Format uptime in human-readable format
  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Format memory as percent used (ESP32-S3 has ~320KB total heap)
  const ESP32_TOTAL_HEAP = 320 * 1024; // 327680 bytes

  const formatMemoryPercent = (freeBytes: number): string => {
    const usedBytes = ESP32_TOTAL_HEAP - freeBytes;
    const percentUsed = Math.round((usedBytes / ESP32_TOTAL_HEAP) * 100);
    return `${percentUsed}%`;
  };

  const formatMemoryKB = (bytes: number): string => {
    return `${Math.round(bytes / 1024)}KB free`;
  };

  // Handle register device
  const handleRegisterDevice = async () => {
    if (!registerDeviceId.trim() || !registerDeviceName.trim()) {
      setRegisterError('Device ID and Name are required');
      return;
    }

    setIsRegistering(true);
    setRegisterError(null);

    try {
      const result = await registerDevice({
        deviceId: registerDeviceId.trim(),
        name: registerDeviceName.trim(),
        type: 'esp32',
        capabilities: {
          has_display: true,
          has_touch: true,
          has_wifi: true,
          gpio_count: 20,
        },
      });

      if (result) {
        setRegisterDeviceDialogOpen(false);
        setRegisterDeviceId('');
        setRegisterDeviceName('');
        // Refresh devices to pick up any status updates
        await refreshDevices();
      } else {
        setRegisterError('Failed to register device');
      }
    } catch (err) {
      setRegisterError(err instanceof Error ? err.message : 'Failed to register device');
    } finally {
      setIsRegistering(false);
    }
  };

  // Load configuration when page loads
  // Note: Removed location.key from dependencies - it changes on every navigation
  // and was causing excessive API requests (rate limiting issues)
  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      const loadedConfig = await loadConfiguration();
      if (!isMounted) return;
      await loadWorkflows(loadedConfig.workflowEngineUrl);
    };

    initialize();

    return () => {
      isMounted = false;
    };
    // Re-run when organization context changes to reload workflows for the correct owner
  }, [projectId, currentContext?.type, currentContext?.orgSlug]);

  const normalizeBaseUrl = (value: string | undefined | null): string | null => {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const withSlash = trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
    if (/^https?:\/\//i.test(withSlash)) return withSlash;
    return new URL(withSlash, window.location.origin).toString();
  };

  const buildWorkflowRequestPath = (
    routePath: string,
    options?: {
      engineUrl?: string;
      query?: Record<string, string>;
    },
  ): string => {
    const base = normalizeBaseUrl(
      options?.engineUrl ??
        config.workflowEngineUrl ??
        defaultWorkflowConfig.workflowEngineUrl,
    );

    if (!base) {
      throw new Error("Workflow engine URL is not configured");
    }

    const normalizedRoutePath = routePath.replace(/^\/+/, '');
    const url = new URL(normalizedRoutePath, base);

    if (options?.query) {
      for (const [key, value] of Object.entries(options.query)) {
        url.searchParams.set(key, value);
      }
    }

    return url.toString();
  };

  const loadConfiguration = async (): Promise<WorkflowConfigForm> => {
    try {
      setIsLoading(true);
      setError(null);

      // TODO: Replace with actual API call to workflow backend (http://localhost:8001)
      // Example: const response = await fetch('http://localhost:8001/api/config');
      // For now, use default configuration
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API call
      setConfig(defaultWorkflowConfig);
      setHasUnsavedChanges(false);

      // Check connection status
      await checkConnection(defaultWorkflowConfig.workflowEngineUrl);

      return defaultWorkflowConfig;
    } catch (err) {
      console.error("Error loading workflow configuration:", err);
      setError("Failed to load workflow configuration");
      return config;
    } finally {
      setIsLoading(false);
    }
  };

  const checkConnection = async (engineUrl?: string) => {
    try {
      setConnectionStatus('connecting');

      const base = normalizeBaseUrl(engineUrl ?? config.workflowEngineUrl ?? defaultWorkflowConfig.workflowEngineUrl);
      if (!base) {
        throw new Error('Workflow engine URL is not configured');
      }

      const healthUrl = new URL('health', base);
      const response = await fetch(healthUrl.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Health check failed with status ${response.status}`);
      }

      const payload = await response.json().catch(() => null);
      if (!payload || payload.success === false) {
        throw new Error('Workflow engine reported an unhealthy status');
      }

      setConnectionStatus('connected');
    } catch (err) {
      console.error("Workflow engine connection check failed:", err);
      setConnectionStatus('error');
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Validate configuration
      if (!config.workflowEngineUrl) {
        throw new Error("Workflow engine URL is required");
      }
      if (config.maxConcurrentWorkflows < 1 || config.maxConcurrentWorkflows > 1000) {
        throw new Error("Max concurrent workflows must be between 1 and 1,000");
      }
      if (config.eventBufferSize < 100 || config.eventBufferSize > 10000) {
        throw new Error("Event buffer size must be between 100 and 10,000");
      }

      // TODO: Replace with actual API call to save configuration
      // Example: await fetch('http://localhost:8001/api/config', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(config)
      // });
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call

      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Error saving workflow configuration:", err);
      setError(err instanceof Error ? err.message : "Failed to save configuration");
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfigChange = <K extends keyof WorkflowConfigForm>(
    field: K,
    value: WorkflowConfigForm[K],
  ) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const loadWorkflows = async (engineUrl?: string) => {
    try {
      setIsLoadingWorkflows(true);
      setWorkflowsError(null);

      const requestPath = buildWorkflowRequestPath(
        buildWorkflowApiRoute(currentContext),
        {
          engineUrl,
          query: { limit: "20" },
        },
      );

      const response = await workflowFetch(requestPath, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      const payload = parseWorkflowListApiResponse(
        await response.json().catch((): unknown => ({ success: false })),
      );

      if (!response.ok || !payload.success) {
        const message = payload.message || payload.error || `Request failed with status ${response.status}`;
        throw new Error(message);
      }

      // Backend returns workflows directly in data array (not data.workflows)
      const workflowsFromApi = Array.isArray(payload.data) ? payload.data : [];

      const normalizedWorkflows = workflowsFromApi.map<WorkflowSummary>((workflow, index) => {
        const id = workflow.id || workflow._id || `workflow-${index}`;
        const ownerName = workflow.created_by?.name || workflow.created_by?.email || 'Unknown owner';

        return {
          id,
          name: workflow.name || 'Untitled Workflow',
          description: workflow.description || 'No description provided.',
          status: workflow.status && isWorkflowSummaryStatus(workflow.status)
            ? workflow.status
            : workflow.status || 'draft',
          type: workflow.type && isWorkflowSummaryType(workflow.type)
            ? workflow.type
            : 'server',
          owner: ownerName,
          runCount: workflow.executionCount ?? 0,
          lastRun: workflow.lastExecutedAt,
          lastUpdated: workflow.updatedAt,
        };
      });

      setWorkflows(normalizedWorkflows);
      setWorkflowsTotal(payload.pagination?.total ?? normalizedWorkflows.length);
    } catch (err) {
      console.error("Error loading workflows:", err);
      setWorkflowsError(err instanceof Error ? err.message : "Failed to load workflows");
      setWorkflows([]);
      setWorkflowsTotal(null);
    } finally {
      setIsLoadingWorkflows(false);
    }
  };

  const handleAddWorkflow = async () => {
    try {
      setIsLoadingWorkflows(true);
      setWorkflowsError(null);

      // Generate unique workflow name
      let workflowName = "New Workflow";
      const existingNames = new Set(workflows.map(w => w.name));

      if (existingNames.has(workflowName)) {
        let counter = 1;
        while (existingNames.has(`${workflowName} ${counter}`)) {
          counter++;
        }
        workflowName = `${workflowName} ${counter}`;
      }

      // Create minimal workflow definition with start and end nodes
      const newWorkflowData = {
        name: workflowName,
        description: "A new workflow",
        definition: {
          nodes: [
            {
              id: "start-1",
              type: "start",
              position: { x: 100, y: 200 },
              data: { label: "Start" },
              config: { retries: 0, onError: "stop", conditions: [] }
            },
            {
              id: "end-1",
              type: "end",
              position: { x: 500, y: 200 },
              data: { label: "End" },
              config: { retries: 0, onError: "stop", conditions: [] }
            }
          ],
          edges: [
            {
              id: "edge-1",
              source: "start-1",
              target: "end-1",
              type: "default"
            }
          ],
          variables: [],
          triggers: [
            {
              id: "manual-trigger-1",
              type: "manual",
              config: {},
              enabled: true
            }
          ],
          version: "1.0.0"
        },
        tags: [],
        metadata: {}
      };

      const response = await workflowFetch(
        buildWorkflowRequestPath(buildWorkflowApiRoute(currentContext)),
        {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newWorkflowData)
        },
      );

      const payload = parseWorkflowMutationApiResponse(
        await response.json().catch((): unknown => ({ success: false })),
      );

      if (!response.ok || !payload.success) {
        const message = payload.message || payload.error || `Request failed with status ${response.status}`;
        throw new Error(message);
      }

      const newWorkflowId = payload.data?.id || payload.data?._id;

      if (!newWorkflowId) {
        throw new Error('No workflow ID returned from server');
      }

      // Navigate to the workflow editor
      navigate(`/workflow-editor/${newWorkflowId}`);
    } catch (err) {
      console.error("Error creating workflow:", err);
      setWorkflowsError(err instanceof Error ? err.message : "Failed to create workflow");
      setIsLoadingWorkflows(false);
    }
  };

  const handleOpenWorkflow = (workflowId: string) => {
    navigate(`/workflow-editor/${workflowId}`);
  };

  const formatDate = (value?: string) => {
    if (!value) return "—";
    try {
      return new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value));
    } catch (err) {
      console.warn("Failed to format date", value, err);
      return value;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "published":
        return "Published";
      case "draft":
        return "Draft";
      case "archived":
        return "Archived";
      case "deprecated":
        return "Deprecated";
      default:
        return status;
    }
  };

  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case "published":
        return "default";
      case "draft":
        return "secondary";
      case "archived":
        return "outline";
      case "deprecated":
        return "destructive";
      default:
        return "outline";
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      if (confirm("You have unsaved changes. Are you sure you want to go back?")) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  const handleOpenRenameDialog = (workflow: WorkflowSummary) => {
    setRenamingWorkflow(workflow);
    setNewWorkflowName(workflow.name);
    setRenameError(null);
    setRenameDialogOpen(true);
  };

  const handleCloseRenameDialog = () => {
    if (!isRenaming) {
      setRenameDialogOpen(false);
      setRenamingWorkflow(null);
      setNewWorkflowName('');
      setRenameError(null);
    }
  };

  const handleRename = async () => {
    if (!renamingWorkflow) return;

    // Validation
    const trimmedName = newWorkflowName.trim();
    if (!trimmedName) {
      setRenameError('Workflow name cannot be empty');
      return;
    }

    if (trimmedName === renamingWorkflow.name) {
      // No change, just close dialog
      handleCloseRenameDialog();
      return;
    }

    try {
      setIsRenaming(true);
      setRenameError(null);

      const response = await workflowFetch(
        buildWorkflowRequestPath(
          buildWorkflowApiRoute(currentContext, renamingWorkflow.id),
        ),
        {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: trimmedName })
        },
      );

      const payload = parseWorkflowMutationApiResponse(
        await response.json().catch((): unknown => ({ success: false })),
      );

      if (!response.ok || !payload.success) {
        const message = payload.message || payload.error || `Request failed with status ${response.status}`;
        throw new Error(message);
      }

      // Update local state
      setWorkflows(prevWorkflows =>
        prevWorkflows.map(w =>
          w.id === renamingWorkflow.id
            ? { ...w, name: trimmedName }
            : w
        )
      );

      // Close dialog
      handleCloseRenameDialog();
    } catch (err) {
      console.error("Error renaming workflow:", err);
      setRenameError(err instanceof Error ? err.message : "Failed to rename workflow");
    } finally {
      setIsRenaming(false);
    }
  };

  // Delete workflow handlers
  const handleOpenDeleteDialog = (workflow: WorkflowSummary) => {
    setWorkflowToDelete(workflow);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
      setDeleteError(null);
    }
  };

  // Transfer workflow handlers
  const handleOpenTransferDialog = (workflow: WorkflowSummary) => {
    setWorkflowToTransfer(workflow);
    setTransferDialogOpen(true);
  };

  const handleTransferComplete = () => {
    // Reload workflows after successful transfer
    loadWorkflows();
  };

  const handleDeleteWorkflow = async () => {
    if (!workflowToDelete) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);

      const response = await workflowFetch(
        buildWorkflowRequestPath(
          buildWorkflowApiRoute(currentContext, workflowToDelete.id),
        ),
        {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        },
      );

      const payload = parseWorkflowMutationApiResponse(
        await response.json().catch((): unknown => ({ success: false })),
      );

      if (!response.ok || !payload.success) {
        const message = payload.message || payload.error || `Request failed with status ${response.status}`;
        throw new Error(message);
      }

      // Remove workflow from local state
      setWorkflows(prevWorkflows =>
        prevWorkflows.filter(w => w.id !== workflowToDelete.id)
      );

      // Update total count
      if (workflowsTotal !== null) {
        setWorkflowsTotal(workflowsTotal - 1);
      }

      // Close dialog
      handleCloseDeleteDialog();
    } catch (err) {
      console.error("Error deleting workflow:", err);
      setDeleteError(err instanceof Error ? err.message : "Failed to delete workflow");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-background sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Workflow className="w-6 h-6" />
                  Workflow Engine Configuration
                </h1>
                <p className="text-sm text-muted-foreground">
                  Configure the TerraMatrix Event Workflow System
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {hasUnsavedChanges && (
                <div className="flex items-center gap-2 text-amber-600 text-sm mr-2">
                  <Info className="w-4 h-4" />
                  Unsaved changes
                </div>
              )}
              <Button
                onClick={handleSave}
                disabled={isLoading || isSaving || !hasUnsavedChanges}
                className={cn(isSaving && "pointer-events-none")}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Configuration
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6 max-w-7xl space-y-6">
        <Card>
          <CardHeader className="gap-4 md:flex md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                <span>Workflows</span>
                {workflowsTotal !== null && (
                  <span className="text-sm font-normal text-muted-foreground">({workflowsTotal})</span>
                )}
              </CardTitle>
              <CardDescription>
                Review existing automations or launch a new workflow for this project.
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadWorkflows()}
                disabled={isLoadingWorkflows}
                className={cn(isLoadingWorkflows && "pointer-events-none")}
              >
                {isLoadingWorkflows ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Refreshing
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </>
                )}
              </Button>
              <Button onClick={handleAddWorkflow} className="gap-2">
                <Plus className="w-4 h-4" />
                New Workflow
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {workflowsError && (
              <div className="flex items-center justify-between rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
                <div className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{workflowsError}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => loadWorkflows()}>
                  Retry
                </Button>
              </div>
            )}

            {isLoadingWorkflows && !workflowsError && (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Loading workflows...</span>
              </div>
            )}

            {!isLoadingWorkflows && !workflowsError && workflows.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-center text-muted-foreground">
                <Workflow className="w-10 h-10" />
                <div>
                  <p className="text-base font-medium text-foreground">No workflows yet</p>
                  <p className="text-sm">Kick things off by creating your first workflow.</p>
                </div>
                <Button onClick={handleAddWorkflow}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Workflow
                </Button>
              </div>
            )}

            {!isLoadingWorkflows && !workflowsError && workflows.length > 0 && (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead className="hidden md:table-cell">Last Run</TableHead>
                      <TableHead className="hidden lg:table-cell">Last Updated</TableHead>
                      <TableHead className="w-[120px] text-right">Executions</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {workflows.map(workflow => (
                      <TableRow
                        key={workflow.id}
                        className="cursor-pointer group"
                        onClick={() => handleOpenWorkflow(workflow.id)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="space-y-1 flex-1">
                              <p className="font-medium text-foreground">{workflow.name}</p>
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {workflow.description}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 hover:bg-accent"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenRenameDialog(workflow);
                              }}
                              title="Rename workflow"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {workflow.type === 'device' ? (
                              <HardDrive className="w-4 h-4 text-orange-500" />
                            ) : (
                              <Cloud className="w-4 h-4 text-blue-500" />
                            )}
                            <span className="text-sm capitalize">{workflow.type}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={getStatusVariant(workflow.status)}
                            className={cn(
                              (workflow.status === "archived" || workflow.status === "deprecated") && "border-dashed text-muted-foreground"
                            )}
                          >
                            {getStatusLabel(workflow.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>{workflow.owner}</TableCell>
                        <TableCell className="hidden md:table-cell">
                          {formatDate(workflow.lastRun)}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {formatDate(workflow.lastUpdated)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {workflow.runCount.toLocaleString()}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleOpenWorkflow(workflow.id)}
                              >
                                <Pencil className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleOpenRenameDialog(workflow)}
                              >
                                <Settings className="w-4 h-4 mr-2" />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleOpenTransferDialog(workflow)}
                              >
                                <ArrowRightLeft className="w-4 h-4 mr-2" />
                                Transfer
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => handleOpenDeleteDialog(workflow)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Device Management Card */}
        <Card>
          <CardHeader className="gap-4 md:flex md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Cpu className="w-5 h-5" />
                <span>Devices</span>
                {deviceCounts.total > 0 && (
                  <span className="text-sm font-normal text-muted-foreground">({deviceCounts.total})</span>
                )}
              </CardTitle>
              <CardDescription>
                Manage ESP32 devices for workflow deployment
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refreshDevices()}
                disabled={devicesLoading}
                className={cn(devicesLoading && "pointer-events-none")}
              >
                {devicesLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Refreshing
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </>
                )}
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => setRegisterDeviceDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Register Device
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Stats Grid */}
            {deviceCounts.total > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/50">
                  <div className="text-2xl font-bold">{deviceCounts.total}</div>
                  <div className="text-sm text-muted-foreground">Total</div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-green-50 border-green-200">
                  <Wifi className="w-5 h-5 text-green-500" />
                  <div className="text-2xl font-bold text-green-700">{deviceCounts.online}</div>
                  <div className="text-sm text-green-600">Online</div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-gray-50 border-gray-200">
                  <WifiOff className="w-5 h-5 text-gray-400" />
                  <div className="text-2xl font-bold text-gray-600">{deviceCounts.offline}</div>
                  <div className="text-sm text-gray-500">Offline</div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200">
                  <Loader2 className="w-5 h-5 text-blue-500" />
                  <div className="text-2xl font-bold text-blue-700">{deviceCounts.deploying}</div>
                  <div className="text-sm text-blue-600">Deploying</div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {devicesLoading && devices.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Loading devices...</span>
              </div>
            )}

            {/* Empty State */}
            {!devicesLoading && devices.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-center text-muted-foreground">
                <Cpu className="w-10 h-10" />
                <div>
                  <p className="text-base font-medium text-foreground">No devices yet</p>
                  <p className="text-sm">Connect an ESP32 device to get started with hardware workflows.</p>
                </div>
              </div>
            )}

            {/* Device Table */}
            {!devicesLoading && devices.length > 0 && (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Device ID</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden lg:table-cell">Telemetry</TableHead>
                      <TableHead className="hidden md:table-cell">Current Workflow</TableHead>
                      <TableHead className="w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {devices.map((device) => (
                      <TableRow
                        key={device.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => {
                          setSelectedDevice(device);
                          setDeviceDetailsDialogOpen(true);
                        }}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Cpu className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">{device.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                            {device.deviceId}
                          </code>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              {getDeviceStatusIcon(device.status)}
                              {device.status === 'error' && device.lastDeploymentError ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge variant={getDeviceStatusVariant(device.status)} className="cursor-help">
                                        {device.status.charAt(0).toUpperCase() + device.status.slice(1)}
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-[300px]">
                                      <p className="text-sm">{device.lastDeploymentError}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <Badge variant={getDeviceStatusVariant(device.status)}>
                                  {device.status.charAt(0).toUpperCase() + device.status.slice(1)}
                                </Badge>
                              )}
                            </div>
                            {device.status === 'error' && device.lastDeploymentError && (
                              <p className="text-xs text-destructive truncate max-w-[200px]" title={device.lastDeploymentError}>
                                {device.lastDeploymentError}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {(() => {
                            const telemetry = getTelemetry(device.deviceId);
                            if (!telemetry) {
                              return <span className="text-muted-foreground text-xs">—</span>;
                            }
                            return (
                              <div className="flex items-center gap-3 text-xs">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className={cn(
                                        "text-muted-foreground",
                                        (ESP32_TOTAL_HEAP - telemetry.freeHeap) / ESP32_TOTAL_HEAP > 0.8 && "text-red-600",
                                        (ESP32_TOTAL_HEAP - telemetry.freeHeap) / ESP32_TOTAL_HEAP > 0.6 && (ESP32_TOTAL_HEAP - telemetry.freeHeap) / ESP32_TOTAL_HEAP <= 0.8 && "text-yellow-600"
                                      )}>
                                        {formatMemoryPercent(telemetry.freeHeap)}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>{formatMemoryKB(telemetry.freeHeap)} • Memory used</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-muted-foreground">
                                        {formatUptime(telemetry.uptime)}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>Uptime</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className={cn(
                                        "text-muted-foreground",
                                        telemetry.rssi > -50 && "text-green-600",
                                        telemetry.rssi <= -50 && telemetry.rssi > -70 && "text-yellow-600",
                                        telemetry.rssi <= -70 && "text-red-600"
                                      )}>
                                        {telemetry.rssi}dBm
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>WiFi signal strength</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className={cn(
                                        "flex items-center gap-1",
                                        telemetry.loggingEnabled ? "text-green-600" : "text-muted-foreground"
                                      )}>
                                        <Terminal className="w-3 h-3" />
                                        {telemetry.loggingEnabled ? "On" : "Off"}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Logging {telemetry.loggingEnabled ? 'enabled' : 'disabled'}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {device.currentWorkflow?.name || (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {(() => {
                                const telemetry = getTelemetry(device.deviceId);
                                const isOnline = device.status === 'online' || device.status === 'running';
                                return (
                                  <>
                                    <DropdownMenuItem
                                      disabled={!isOnline}
                                      onClick={async () => {
                                        const newEnabled = !(telemetry?.loggingEnabled ?? true);
                                        await setDeviceLogging(device.deviceId, newEnabled);
                                      }}
                                    >
                                      <Terminal className="w-4 h-4 mr-2" />
                                      {telemetry?.loggingEnabled ? 'Disable Logging' : 'Enable Logging'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      disabled={!isOnline}
                                      onClick={async () => {
                                        if (confirm(`Are you sure you want to restart "${device.name}"?`)) {
                                          await restartDevice(device.deviceId);
                                        }
                                      }}
                                    >
                                      <RotateCcw className="w-4 h-4 mr-2" />
                                      Restart Device
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="text-destructive focus:text-destructive"
                                      onClick={() => {
                                        if (confirm(`Are you sure you want to delete "${device.name}"?`)) {
                                          deleteDevice(device.id);
                                        }
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                );
                              })()}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Connection Status Banner */}
        <Card className={cn(
          connectionStatus === 'connected' && "border-green-200 bg-green-50",
          connectionStatus === 'connecting' && "border-blue-200 bg-blue-50",
          connectionStatus === 'error' && "border-red-200 bg-red-50",
          connectionStatus === 'disconnected' && "border-gray-200 bg-gray-50"
        )}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {connectionStatus === 'connected' && <CheckCircle className="w-5 h-5 text-green-600" />}
                {connectionStatus === 'connecting' && <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />}
                {connectionStatus === 'error' && <AlertTriangle className="w-5 h-5 text-red-600" />}
                {connectionStatus === 'disconnected' && <Info className="w-5 h-5 text-gray-600" />}
                <div>
                  <div className={cn(
                    "font-medium",
                    connectionStatus === 'connected' && "text-green-700",
                    connectionStatus === 'connecting' && "text-blue-700",
                    connectionStatus === 'error' && "text-red-700",
                    connectionStatus === 'disconnected' && "text-gray-700"
                  )}>
                    {connectionStatus === 'connected' && 'Connected to Workflow Engine'}
                    {connectionStatus === 'connecting' && 'Connecting to Workflow Engine...'}
                    {connectionStatus === 'error' && 'Failed to connect to Workflow Engine'}
                    {connectionStatus === 'disconnected' && 'Not connected'}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {config.workflowEngineUrl}
                  </div>
                </div>
              </div>
              {connectionStatus === 'error' && (
                <Button variant="outline" size="sm" onClick={() => checkConnection()}>
                  Retry Connection
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {isLoading ? (
          <Card>
            <CardContent className="py-12">
              <div className="flex flex-col items-center justify-center gap-4">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-muted-foreground">Loading configuration...</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="sources" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="sources" className="flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Event Sources
              </TabsTrigger>
              <TabsTrigger value="executions" className="flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Executions
              </TabsTrigger>
            </TabsList>

            {/* Event Sources Tab */}
            <TabsContent value="sources" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    General Settings
                  </CardTitle>
                  <CardDescription>
                    Configure basic workflow engine settings and connection parameters.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="enabled">Enable Workflow Engine</Label>
                      <p className="text-sm text-muted-foreground">
                        Activate automated workflow processing for this project
                      </p>
                    </div>
                    <Switch
                      id="enabled"
                      checked={config.enabled}
                      onCheckedChange={(checked) => handleConfigChange('enabled', checked)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="workflowEngineUrl">Workflow Engine URL</Label>
                    <Input
                      id="workflowEngineUrl"
                      type="url"
                      value={config.workflowEngineUrl}
                      onChange={(e) => handleConfigChange('workflowEngineUrl', e.target.value)}
                      placeholder={WORKFLOW_API_PLACEHOLDER}
                      disabled={!config.enabled}
                    />
                    <p className="text-xs text-muted-foreground">
                      The URL of the TerraMatrix Workflow Engine backend service
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="organizationId">Organization ID</Label>
                    <Input
                      id="organizationId"
                      type="text"
                      value={config.organizationId}
                      onChange={(e) => handleConfigChange('organizationId', e.target.value)}
                      placeholder="Enter organization ID"
                      disabled={!config.enabled}
                    />
                    <p className="text-xs text-muted-foreground">
                      Multi-tenant organization identifier for workflow isolation
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="enableTenantIsolation">Tenant Isolation</Label>
                      <p className="text-sm text-muted-foreground">
                        Ensure workflows are isolated by organization
                      </p>
                    </div>
                    <Switch
                      id="enableTenantIsolation"
                      checked={config.enableTenantIsolation}
                      onCheckedChange={(checked) => handleConfigChange('enableTenantIsolation', checked)}
                      disabled={!config.enabled}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Event Source Configuration
                  </CardTitle>
                  <CardDescription>
                    Enable or disable different types of event sources for workflow triggering.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {eventSourceOptions.map((source) => {
                    const configKey = getEventSourceConfigKey(source.id);
                    if (!configKey) {
                      return null;
                    }

                    return (
                      <div key={source.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="space-y-0.5">
                          <Label htmlFor={source.id}>{source.label}</Label>
                          <p className="text-sm text-muted-foreground">
                            {source.description}
                          </p>
                        </div>
                        <Switch
                          id={source.id}
                          checked={config[configKey]}
                          onCheckedChange={(checked) =>
                            handleConfigChange(configKey, checked)
                          }
                          disabled={!config.enabled}
                        />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Performance Settings
                  </CardTitle>
                  <CardDescription>
                    Configure performance and concurrency settings for workflow execution.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="eventBufferSize">Event Buffer Size</Label>
                      <Input
                        id="eventBufferSize"
                        type="number"
                        min="100"
                        max="10000"
                        value={config.eventBufferSize}
                        onChange={(e) => handleConfigChange('eventBufferSize', parseInt(e.target.value, 10))}
                        disabled={!config.enabled}
                      />
                      <p className="text-xs text-muted-foreground">
                        Number of events to buffer (100-10,000)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="processingConcurrency">Processing Concurrency</Label>
                      <Input
                        id="processingConcurrency"
                        type="number"
                        min="1"
                        max="100"
                        value={config.processingConcurrency}
                        onChange={(e) => handleConfigChange('processingConcurrency', parseInt(e.target.value, 10))}
                        disabled={!config.enabled}
                      />
                      <p className="text-xs text-muted-foreground">
                        Concurrent event processing threads (1-100)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="maxConcurrentWorkflows">Max Concurrent Workflows</Label>
                      <Input
                        id="maxConcurrentWorkflows"
                        type="number"
                        min="1"
                        max="1000"
                        value={config.maxConcurrentWorkflows}
                        onChange={(e) => handleConfigChange('maxConcurrentWorkflows', parseInt(e.target.value, 10))}
                        disabled={!config.enabled}
                      />
                      <p className="text-xs text-muted-foreground">
                        Maximum simultaneous workflows (1-1,000)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="retryAttempts">Retry Attempts</Label>
                      <Input
                        id="retryAttempts"
                        type="number"
                        min="0"
                        max="10"
                        value={config.retryAttempts}
                        onChange={(e) => handleConfigChange('retryAttempts', parseInt(e.target.value, 10))}
                        disabled={!config.enabled}
                      />
                      <p className="text-xs text-muted-foreground">
                        Failed workflow retry attempts (0-10)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="workflowTimeout">Workflow Timeout (seconds)</Label>
                      <Input
                        id="workflowTimeout"
                        type="number"
                        min="60"
                        max="86400"
                        value={config.workflowTimeout}
                        onChange={(e) => handleConfigChange('workflowTimeout', parseInt(e.target.value, 10))}
                        disabled={!config.enabled}
                      />
                      <p className="text-xs text-muted-foreground">
                        Default timeout for workflows (60-86400s)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="temporalNamespace">Temporal Namespace</Label>
                      <Input
                        id="temporalNamespace"
                        type="text"
                        value={config.temporalNamespace}
                        onChange={(e) => handleConfigChange('temporalNamespace', e.target.value)}
                        disabled={!config.enabled}
                      />
                      <p className="text-xs text-muted-foreground">
                        Temporal.io namespace for workflow isolation
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Executions Tab */}
            <TabsContent value="executions" className="space-y-6 mt-6">
              <Card>
                <CardContent className="py-12">
                  <div className="flex flex-col items-center justify-center text-center space-y-4">
                    <Activity className="w-16 h-16 text-muted-foreground" />
                    <div>
                      <h3 className="text-xl font-semibold">Execution Monitor</h3>
                      <p className="text-sm text-muted-foreground mt-2 max-w-md">
                        Monitor running and completed workflow executions. View execution logs, performance metrics,
                        and manage long-running processes.
                      </p>
                    </div>
                    <Button variant="outline" disabled>
                      <Activity className="w-4 h-4 mr-2" />
                      Coming Soon
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      TODO: Integrate with workflow backend API to display execution status and logs
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={handleCloseRenameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Workflow</DialogTitle>
            <DialogDescription>
              Enter a new name for "{renamingWorkflow?.name}".
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="workflow-name">Workflow Name</Label>
              <Input
                id="workflow-name"
                value={newWorkflowName}
                onChange={(e) => setNewWorkflowName(e.target.value)}
                placeholder="Enter workflow name"
                disabled={isRenaming}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isRenaming) {
                    handleRename();
                  }
                }}
              />
            </div>

            {renameError && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4" />
                <span>{renameError}</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseRenameDialog}
              disabled={isRenaming}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRename}
              disabled={isRenaming || !newWorkflowName.trim()}
            >
              {isRenaming ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Renaming...
                </>
              ) : (
                'Rename'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Workflow Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={handleCloseDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Workflow</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{workflowToDelete?.name}"?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deleteError && (
            <div className="flex items-center gap-2 text-sm text-destructive py-2">
              <AlertTriangle className="h-4 w-4" />
              <span>{deleteError}</span>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseDeleteDialog}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteWorkflow}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Workflow Dialog */}
      {workflowToTransfer && (
        <WorkflowTransferDialog
          open={transferDialogOpen}
          onOpenChange={setTransferDialogOpen}
          workflowId={workflowToTransfer.id}
          workflowName={workflowToTransfer.name}
          currentOwnerType={currentContext?.type === 'organization' ? 'Organization' : 'User'}
          currentOwnerId={currentContext?.id || ''}
          currentOwnerName={currentContext?.displayName}
          workflowEngineUrl={normalizeBaseUrl(config.workflowEngineUrl ?? defaultWorkflowConfig.workflowEngineUrl) || ''}
          onTransferComplete={handleTransferComplete}
        />
      )}

      {/* Register Device Dialog */}
      <Dialog open={registerDeviceDialogOpen} onOpenChange={setRegisterDeviceDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cpu className="w-5 h-5" />
              Register ESP32 Device
            </DialogTitle>
            <DialogDescription>
              Enter the device ID and a friendly name. The device ID is shown on the ESP32 screen
              and typically starts with "esp32-".
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="deviceId">Device ID</Label>
              <Input
                id="deviceId"
                placeholder="esp32-885721bbb10c"
                value={registerDeviceId}
                onChange={(e) => setRegisterDeviceId(e.target.value)}
                disabled={isRegistering}
              />
              <p className="text-xs text-muted-foreground">
                The unique identifier displayed on your ESP32 device
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="deviceName">Device Name</Label>
              <Input
                id="deviceName"
                placeholder="Farm Controller"
                value={registerDeviceName}
                onChange={(e) => setRegisterDeviceName(e.target.value)}
                disabled={isRegistering}
              />
              <p className="text-xs text-muted-foreground">
                A friendly name to identify this device
              </p>
            </div>
            {registerError && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="w-4 h-4" />
                {registerError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRegisterDeviceDialogOpen(false);
                setRegisterDeviceId('');
                setRegisterDeviceName('');
                setRegisterError(null);
              }}
              disabled={isRegistering}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRegisterDevice}
              disabled={isRegistering || !registerDeviceId.trim() || !registerDeviceName.trim()}
            >
              {isRegistering ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Registering...
                </>
              ) : (
                'Register Device'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Device Details Dialog */}
      <Dialog open={deviceDetailsDialogOpen} onOpenChange={setDeviceDetailsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cpu className="w-5 h-5" />
              Device Details
            </DialogTitle>
            <DialogDescription>
              {selectedDevice?.name} - {selectedDevice?.deviceId}
            </DialogDescription>
          </DialogHeader>

          {selectedDevice && (
            <div className="space-y-4 py-4">
              {/* Status Section */}
              <div className="space-y-2">
                <Label className="text-muted-foreground">Status</Label>
                <div className="flex items-center gap-2">
                  {getDeviceStatusIcon(selectedDevice.status)}
                  <Badge variant={getDeviceStatusVariant(selectedDevice.status)}>
                    {selectedDevice.status.charAt(0).toUpperCase() + selectedDevice.status.slice(1)}
                  </Badge>
                </div>
              </div>

              {/* Error Section - Only shown if there's an error */}
              {selectedDevice.status === 'error' && selectedDevice.lastDeploymentError && (
                <div className="space-y-2">
                  <Label className="text-destructive flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Deployment Error
                  </Label>
                  <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30">
                    <p className="text-sm text-destructive whitespace-pre-wrap break-words">
                      {selectedDevice.lastDeploymentError}
                    </p>
                  </div>
                </div>
              )}

              {/* Device Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-muted-foreground">Device ID</Label>
                  <code className="block text-xs bg-muted px-2 py-1 rounded">
                    {selectedDevice.deviceId}
                  </code>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground">Type</Label>
                  <p className="text-sm">{selectedDevice.type.toUpperCase()}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground">Last Seen</Label>
                  <p className="text-sm">{formatDate(selectedDevice.lastSeen)}</p>
                </div>
                {selectedDevice.firmwareVersion && (
                  <div className="space-y-1">
                    <Label className="text-muted-foreground">Firmware</Label>
                    <p className="text-sm">{selectedDevice.firmwareVersion}</p>
                  </div>
                )}
              </div>

              {/* Current Workflow */}
              <div className="space-y-2">
                <Label className="text-muted-foreground">Current Workflow</Label>
                {selectedDevice.currentWorkflow ? (
                  <div className="p-2 rounded-md bg-muted">
                    <p className="text-sm font-medium">{selectedDevice.currentWorkflow.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Status: {selectedDevice.currentWorkflow.status}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No workflow deployed</p>
                )}
              </div>

              {/* Capabilities */}
              <div className="space-y-2">
                <Label className="text-muted-foreground">Capabilities</Label>
                <div className="flex flex-wrap gap-1">
                  {selectedDevice.capabilities.has_display && (
                    <Badge variant="outline" className="text-xs">Display</Badge>
                  )}
                  {selectedDevice.capabilities.has_touch && (
                    <Badge variant="outline" className="text-xs">Touch</Badge>
                  )}
                  {selectedDevice.capabilities.has_wifi && (
                    <Badge variant="outline" className="text-xs">WiFi</Badge>
                  )}
                  {selectedDevice.capabilities.has_bluetooth && (
                    <Badge variant="outline" className="text-xs">Bluetooth</Badge>
                  )}
                  {selectedDevice.capabilities.gpio_count && (
                    <Badge variant="outline" className="text-xs">
                      GPIO: {selectedDevice.capabilities.gpio_count}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Execution Log */}
              <div className="space-y-2">
                <Label className="text-muted-foreground flex items-center gap-2">
                  <Terminal className="w-4 h-4" />
                  Execution Log
                </Label>
                <DeviceExecutionLog
                  deviceId={selectedDevice.deviceId}
                  maxHeight="200px"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeviceDetailsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
